/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { getConfig } from './config.js';
import { emit, on } from './events.js';
import { isLocalhost } from './links.js';
import {
    contentPreprocessors,
    setConsentPolicy,
} from './content-preprocessors.js';
import { PopupPlugin } from './popup-plugin.js';

/*
 * Cookies, and the consent that governs them.
 *
 * `cookie` is the storage primitive. `cookieConsent` is the policy on top:
 * which categories the reader has agreed to, the dialog that asks, and the
 * events other modules listen for when the answer changes.
 *
 * It imports the content preprocessors and they import it back. That is
 * deliberate rather than accidental: blocking third-party media is a
 * consent decision, and restoring it is what granting consent means.
 */

/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/* Basic cookie utilities */

export const cookie = {};

cookie.create = (name, value, options = {}) => {
    let expires = '';
    if (options.days) {
        const millisecs = options.days * 24 * 60 * 60 * 1000;
        const date = new Date();
        date.setTime(date.getTime() + millisecs);
        expires = `; expires=${date.toUTCString()}`;
    }

    const path = `; path=${options.path || '/'}`;

    let secure = '';
    // Both branches below set this, so it needs no starting value.
    let samesite;
    if (!isLocalhost()) {
        secure = '; secure';
        samesite = `; SameSite=${options.samesite || 'None'}`;
    } else {
        // On localhost we default to Lax so the cookie works without https
        samesite = `; SameSite=${options.samesite || 'Lax'}`;
    }

    const encoded = encodeURIComponent(String(value));
    document.cookie = `${name}=${encoded}${expires}${path}${secure}${samesite}`;
};

cookie.read = (name) => {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
            return decodeURIComponent(c.substring(nameEQ.length, c.length));
        }
    }
    return null;
};

cookie.erase = (name, options) => {
    cookie.create(name, '', { ...options, days: -1 });
};

/* Cookie Consent */

export const cookieConsent = {};

// The preprocessors ask this before letting third-party media load.
// Handing it over keeps the dependency running one way: importing each
// other made a cycle, and the page died on a `ReferenceError`.
setConsentPolicy(cookieConsent);

cookieConsent.initialized = false;
cookieConsent.enabled = true;
cookieConsent._cookieName = 'cookie-consent';
cookieConsent.consentGiven = {
    essential: true,
    statistics: false,
    external: false
};
// Keep a reference to the popup instance for programmatic control
cookieConsent._popup = null;
cookieConsent.hasConsented = (category) => {
    // If consent is disabled entirely, treat as allowed.
    if (!cookieConsent.enabled) return true;
    // If already initialized, read in-memory state.
    if (cookieConsent.initialized) {
        return !!cookieConsent.consentGiven[category];
    }
    // Before init: peek saved cookie to avoid transient blocking.
    const saved = cookieConsent._readSavedConsent?.();
    return !!saved?.[category];
};

// --- Consent content ---

cookieConsent.createConsentHTML = (uiConfig) => {
    const { title, intro, categories, labels, urls } = uiConfig;

    let categoriesHTML = '';
    for (const category of categories) {
        const checked =
            category.id === 'essential'
                ? 'checked'
                : cookieConsent.consentGiven[category.id]
                  ? 'checked'
                  : '';
        const disabled = category.locked ? 'disabled' : '';
        categoriesHTML += `
            <div class="custom-checkbox" data-category="${category.id}">
                <label class="custom-checkbox__container">
                    <input name="${category.id}" ${checked} ${disabled} type="checkbox" class="custom-checkbox__input">
                    <span class="custom-checkbox__checkmark"></span>
                    <div class="custom-checkbox__content">
                        <div class="custom-checkbox__title">${category.label}</div>
                        <div class="custom-checkbox__description">${category.description}</div>
                    </div>
                </label>
            </div>
        `;
    }

    // `data-nosnippet`: consent copy says nothing about the map, and
    // search results for map pages have quoted it in place of the content.
    return `
        <div data-nosnippet>
        <h1 id="cookie-consent-title">${title}</h1>
        ${intro}
        <hr>
        ${categoriesHTML}
        <hr>
        <div class="button-area">
            <button type="button" class="button cc-btn-save">${labels.saveSettings}</button>
            <button type="button" class="button cc-btn-accept-all">${labels.acceptAll}</button>
            <button type="button" class="button cc-btn-accept-essential">${labels.acceptEssentialOnly}</button>
        </div>
        <hr>
        <div class="links-area">
            <a target="_blank" href="${urls.privacyPolicy}">${labels.privacyPolicy}</a>
            <a target="_blank" href="${urls.imprint}">${labels.imprint}</a>
        </div>
        </div>
    `;
};

// --- Logic helpers ---

cookieConsent._writeCookie = (consentObj) => {
    try {
        const consent = JSON.stringify(consentObj);
        // 180 days by default
        cookie.create(
            cookieConsent._cookieName,
            consent,
            {
                days: 180,
                path: '/'
            }
        );
    } catch (e) {
        // Fallback: session cookie
        cookie.create(
            cookieConsent._cookieName,
            String(consentObj),
            {
                path: '/'
            }
        );
    }
};

cookieConsent._callAllowFor = (category) => {
    emit(document, `cookieConsent:allow:${category}`);
};

cookieConsent._callRevokeFor = (category) => {
    emit(document, `cookieConsent:revoke:${category}`);
};

cookieConsent._applyConsent = (
    nextConsent,
    { fireCallbacks = true } = {}
) => {
    const prev = { ...cookieConsent.consentGiven };

    // Essential is always true
    nextConsent.essential = true;

    cookieConsent.consentGiven = nextConsent;
    cookieConsent.initialized = true;

    // Persist
    cookieConsent._writeCookie(nextConsent);

    if (fireCallbacks) {
        // Compare prev/next for each optional category
        ['statistics', 'external'].forEach((cat) => {
            if (prev[cat] !== nextConsent[cat]) {
                if (nextConsent[cat]) {
                    cookieConsent._callAllowFor(cat);
                } else {
                    cookieConsent._callRevokeFor(cat);
                }
            } else {
                // If no previous consent saved (first run) and value is true, still fire `allow`
                if (
                    prev.essential === true &&
                    prev.statistics === false &&
                    prev.external === false
                ) {
                    if (nextConsent[cat])
                        cookieConsent._callAllowFor(cat);
                }
            }
        });
    }
};

cookieConsent._readSavedConsent = () => {
    const raw = cookie.read(cookieConsent._cookieName);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        // Ensure essential is true
        return { ...parsed, essential: true };
    } catch (e) {
        return null;
    }
};

cookieConsent._updateUIFromConsent = (ui) => {
    if (!ui) return;
    ['statistics', 'external'].forEach((category) => {
        const catCheckbox = ui.querySelector(`input[name="${category}"]`);
        if (catCheckbox)
            catCheckbox.checked =
                !!cookieConsent.consentGiven[category];
    });
};

// Public utility for opening settings later (e.g., from a footer link)
cookieConsent.showSettings = () => {
    const popup = cookieConsent._popup;
    if (!popup) {
        console.warn('Cookie Consent popup is not initialized yet.');
        return;
    }
    // Refresh UI from the current state before opening
    const ui = popup.body;
    cookieConsent._updateUIFromConsent(ui);
    // Use fresh markup from the stored UI config
    const consentHTML = cookieConsent.createConsentHTML(
        cookieConsent._uiConfig
    );
    popup.open(consentHTML);
};

// --- Init and wiring ---

cookieConsent.init = (root, enabled, uiConfig) => {
    // Defined by the analytics snippet the backend renders into the page.
    // A tenant without analytics has none.
    if (typeof window.loadTracking === 'function') {
        on('cookieConsent:allow:statistics', window.loadTracking);
    }

    cookieConsent.enabled = enabled === true;
    // Store UI config for future renders
    cookieConsent._uiConfig = uiConfig;

    // Create UI
    const UI_ID = 'cookie-consent';

    // Prepare popup (single, shared instance)
    if (!cookieConsent._popup) {
        cookieConsent._popup = new PopupPlugin({
            root: root,
            id: UI_ID,
            color: '#A6DDDD',
            topOffset: 20,
            bottomOffset: 20,
            closeOnOverlayClick: false,
            // The close button dismisses without recording a decision, so
            // Escape does nothing the dialog did not already allow -- and a
            // modal that traps Tab owes the keyboard the same way out.
            closeOnEsc: true,
            ariaLabelledBy: 'cookie-consent-title',
            closeLabel: getConfig()?.uiText?.a11y?.closeDialog,
            onOpen: () => {
                const ui = cookieConsent._popup.body;

                const btnSave = ui.querySelector('.cc-btn-save');
                const btnAll = ui.querySelector('.cc-btn-accept-all');
                const btnEssentialOnly = ui.querySelector(
                    '.cc-btn-accept-essential'
                );

                if (btnSave) {
                    btnSave.addEventListener('click', () => {
                        const consent = { essential: true };
                        ['statistics', 'external'].forEach((category) => {
                            consent[category] =
                                ui.querySelector(`input[name="${category}"]`)
                                    ?.checked || false;
                        });
                        cookieConsent._applyConsent(consent, {
                            fireCallbacks: true
                        });
                        cookieConsent._popup.close();
                    });
                }

                if (btnAll) {
                    btnAll.addEventListener('click', () => {
                        const consent = { essential: true };
                        ['statistics', 'external'].forEach((category) => {
                            consent[category] = true;
                        });
                        cookieConsent._applyConsent(consent, {
                            fireCallbacks: true
                        });
                        // Reflect immediately in UI for visual feedback
                        cookieConsent._updateUIFromConsent(ui);
                        cookieConsent._popup.close();
                    });
                }

                if (btnEssentialOnly) {
                    btnEssentialOnly.addEventListener('click', () => {
                        const consent = { essential: true };
                        ['statistics', 'external'].forEach((category) => {
                            consent[category] = false;
                        });
                        cookieConsent._applyConsent(consent, {
                            fireCallbacks: true
                        });
                        cookieConsent._updateUIFromConsent(ui);
                        cookieConsent._popup.close();
                    });
                }
            },
            onClose: () => {
                // Persist whatever is currently selected when the user closes via X/overlay
                const consent = { essential: true };
                const ui = cookieConsent._popup.body;
                ['statistics', 'external'].forEach((category) => {
                    consent[category] =
                        ui.querySelector(`input[name="${category}"]`)
                            ?.checked || false;
                });
                cookieConsent._applyConsent(consent, {
                    fireCallbacks: true
                });
            }
        });
    }

    // Public trigger (for changing settings later)
    const trigger = document.querySelector('.cc-trigger');
    if (trigger) {
        trigger.addEventListener('click', () => {
            const popup = cookieConsent._popup;
            if (!popup) return;
            const ui = popup.body;
            // Refresh UI from the current state before opening
            cookieConsent._updateUIFromConsent(ui);
            const consentHTML = cookieConsent.createConsentHTML(
                cookieConsent._uiConfig
            );
            popup.open(consentHTML);
        });
    }

    // If consent is disabled entirely, call `allow` callbacks and exit.
    if (!cookieConsent.enabled) {
        window.addEventListener('load', () => {
            ['statistics', 'external'].forEach((category) => {
                cookieConsent._callAllowFor(category);
            });
        });
        cookieConsent.initialized = true;
        return;
    }

    // Wire default (un)blocking behavior to consent changes.
    try {
        on('cookieConsent:allow:external', () =>
            contentPreprocessors?.restoreBlockedExternal?.(document)
        );
        on('cookieConsent:revoke:external', () => {
            // Re-apply the policy per container:
            //  - [data-cc-external="hide"] → hide
            //  - [data-cc-external="placeholder"] → placeholder
            if (contentPreprocessors?.reapplyPolicy) {
                contentPreprocessors.reapplyPolicy(document);
            } else {
                contentPreprocessors?.defaults?.blockExternalMediaWithPlaceholders?.(
                    document
                );
            }
        });
    } catch (err) {
        console.error(
            'Error setting up default content preprocessor callbacks:',
            err
        );
    }

    // If consent is enabled and there is no saved consent yet,
    // proactively block any external media that might already be in the DOM.
    // This closes a race where third-party requests could start before the UI opens.
    try {
        if (
            cookieConsent.enabled &&
            !cookieConsent._readSavedConsent()
        ) {
            contentPreprocessors?.defaults?.reapplyPolicy?.(
                document
            );
        }
    } catch (err) {
        console.error('Error early blocking external media.', err);
    }

    // Load saved consent (if any), else show UI immediately
    const saved = cookieConsent._readSavedConsent();
    const ui = cookieConsent._popup?.body;
    if (saved) {
        // Apply saved state without double-firing callbacks on load
        cookieConsent._applyConsent(saved, { fireCallbacks: true });
        // Make sure UI mirrors saved choice
        if (ui) {
            cookieConsent._updateUIFromConsent(ui);
        }
    } else if (!uiConfig.suppressAutoOpen) {
        // No consent yet — open on load, unless the user navigated here via
        // an imprint/privacy-policy deep link (suppressAutoOpen=true).
        window.addEventListener('load', () => {
            const popup = cookieConsent._popup;
            if (!popup) return;
            const uiNow = popup.body;
            // Ensure default switches reflect initial (all off except essential)
            cookieConsent._updateUIFromConsent(uiNow);
            ['statistics', 'external'].forEach((category) => {
                const categoryCheckbox = uiNow.querySelector(
                    `input[name="${category}"]`
                );
                if (categoryCheckbox) categoryCheckbox.checked = false;
            });

            // Open the popup with current UI config
            const consentHTML = cookieConsent.createConsentHTML(
                cookieConsent._uiConfig
            );
            popup.open(consentHTML);
        });
    }
};
